import { lazy, Suspense, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button, SheetGrip } from '@/components/ui';
import { Icon } from '@/components/Icon';
import { VisibilityPicker } from '@/components/VisibilityPicker';
import { api, type FeedActivity } from '@/lib/api';
import { deleteActivityFromHealth } from '@/lib/healthStore';
import { useBackHandler } from '@/hooks/useBackHandler';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { useProfile } from '@/hooks/ProfileProvider';
import { useT } from '@/i18n/react';
import type { Visibility } from '@/types';
import './activityCardMenu.css';

const ShareSheet = lazy(() => import('@/components/ShareSheet').then((m) => ({ default: m.ShareSheet })));
const ActivityExportSheet = lazy(() =>
  import('@/components/ActivityExportSheet').then((m) => ({ default: m.ActivityExportSheet })),
);

/**
 * ⋯ menu of the user's OWN activity on a feed card (docs/02 → ⋯ menü):
 * Edit · Visibility · Delete. Edit opens the detail screen's editor; the other
 * two run here so the card needs no round trip. The feed refreshes by itself —
 * `api.ts` invalidates `['activities']` after every non-GET request.
 */
type View = 'menu' | 'visibility' | 'delete' | 'done';

export function OwnActivityMenu({ item, onClose }: { item: FeedActivity; onClose: () => void }) {
  const t = useT().ownMenu;
  const tShare = useT().share;
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const navigate = useNavigate();
  const { reload: reloadProfile } = useProfile();
  const [view, setView] = useState<View>('menu');
  const [visibility, setVisibility] = useState<Visibility>(item.visibility ?? 'everyone');
  const [doneText, setDoneText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useBackHandler(true, () => onClose());
  const panelRef = useSwipeToClose<HTMLDivElement>(onClose, true, view);

  function run(action: () => Promise<unknown>, message: string, onFail?: () => void) {
    setBusy(true);
    setError('');
    action()
      .then(() => {
        setDoneText(message);
        setView('done');
      })
      .catch((err: unknown) => {
        onFail?.();
        setError(err instanceof Error ? err.message : t.failed);
      })
      .finally(() => setBusy(false));
  }

  function chooseVisibility(next: Visibility) {
    if (next === visibility) return;
    const previous = visibility;
    setVisibility(next);
    // A failed save must not leave the picker showing a value that was not stored.
    run(() => api.updateActivity(item.id, { visibility: next }), t.visibilitySaved, () =>
      setVisibility(previous),
    );
  }

  // The share sheet replaces the menu — one sheet on screen at a time.
  if (shareOpen) {
    return (
      <Suspense fallback={null}>
        <ShareSheet activityId={item.id} onClose={onClose} />
      </Suspense>
    );
  }
  if (exportOpen) {
    return (
      <Suspense fallback={null}>
        <ActivityExportSheet activityId={item.id} onClose={onClose} />
      </Suspense>
    );
  }

  // Portal (DECISIONS #52): the feed sits inside PullToRefresh's transformed layer.
  return createPortal(
    <div className="amenu" role="dialog" aria-label={t.title}>
      <button type="button" className="amenu__scrim" aria-label={t.back} onClick={onClose} />
      {view === 'done' ? (
        <div ref={panelRef} className="amenu__panel amenu__panel--done" role="status">
          <SheetGrip />
          <span className="amenu__done-icon">
            <Icon name="check" size={32} />
          </span>
          <span className="amenu__title">{doneText}</span>
          <Button block onClick={onClose}>{t.back}</Button>
        </div>
      ) : (
        <div ref={panelRef} className="amenu__panel">
          <SheetGrip />
          <span className="amenu__title">{view === 'delete' ? t.deleteTitle : t.title}</span>

          {view === 'menu' ? (
            <>
              <button
                type="button"
                className="amenu__item"
                onClick={() => navigate(`/aktivitas/${item.id}`, { state: { edit: true } })}
              >
                {t.edit}
              </button>
              <button type="button" className="amenu__item" onClick={() => setShareOpen(true)}>
                {tShare.open}
              </button>
              <button type="button" className="amenu__item" onClick={() => setExportOpen(true)}>
                {t.export}
              </button>
              <button type="button" className="amenu__item" onClick={() => setView('visibility')}>
                {t.visibility}
              </button>
              <button
                type="button"
                className="amenu__item amenu__item--danger"
                onClick={() => setView('delete')}
              >
                {t.delete}
              </button>
            </>
          ) : null}

          {view === 'visibility' ? (
            <VisibilityPicker value={visibility} onChange={chooseVisibility} disabled={busy} />
          ) : null}

          {view === 'delete' ? (
            <>
              <p className="amenu__lead">{t.deleteLead}</p>
              <Button
                block
                variant="danger"
                loading={busy}
                onClick={() =>
                  run(() => api.deleteActivity(item.id).then(() => {
                    deleteActivityFromHealth(item.id);
                    return reloadProfile();
                  }), t.deleted)
                }
              >
                {t.deleteConfirm}
              </Button>
            </>
          ) : null}

          {error ? <p className="field__error" role="alert">{error}</p> : null}
          <button
            type="button"
            className="amenu__item amenu__item--cancel"
            disabled={busy}
            onClick={view === 'menu' ? onClose : () => { setError(''); setView('menu'); }}
          >
            {t.back}
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
